// Persisted "seen it once" flags for the earliest player-facing onboarding
// nudges -- not real game state (unlike learnedRunes.ts) and not a player
// preference (unlike settings.ts), just enough memory to stop repeating a
// hint once its job is done. Reset alongside New Game, same as learned runes
// and the notebook -- "starting over" should mean seeing these again too.

const INTERACTED_KEY = 'rune-world-game-tutorial-interacted';
const EQUIP_TUTORIAL_KEY = 'rune-world-game-tutorial-equip-seen';
const CAST_TUTORIAL_KEY = 'rune-world-game-tutorial-cast-seen';
const MODIFIER_TUTORIAL_KEY = 'rune-world-game-tutorial-modifier-seen';
const TRANSFER_TUTORIAL_KEY = 'rune-world-game-tutorial-transfer-seen';
const NOTEBOOK_TUTORIAL_KEY = 'rune-world-game-tutorial-notebook-seen';

let hasInteractedOnce = localStorage.getItem(INTERACTED_KEY) === 'true';
let hasSeenEquipTutorial = localStorage.getItem(EQUIP_TUTORIAL_KEY) === 'true';
let hasSeenCastTutorial = localStorage.getItem(CAST_TUTORIAL_KEY) === 'true';
let hasSeenModifierTutorial = localStorage.getItem(MODIFIER_TUTORIAL_KEY) === 'true';
let hasSeenTransferTutorial = localStorage.getItem(TRANSFER_TUTORIAL_KEY) === 'true';
let hasSeenNotebookTutorial = localStorage.getItem(NOTEBOOK_TUTORIAL_KEY) === 'true';

// Gates room0/room1's persistent "press Interact" canvas hint (see
// GameScene) -- true forever after the very first successful Interact,
// wherever in the game that happens to occur.
export function getHasInteractedOnce(): boolean {
  return hasInteractedOnce;
}

export function markInteractedOnce(): void {
  if (hasInteractedOnce) return;
  hasInteractedOnce = true;
  localStorage.setItem(INTERACTED_KEY, 'true');
}

// Gates the first of two one-time onboarding panels (see ui.ts), shown the
// instant a player holds both Energy and Project -- whichever room2
// bookshelf they read second is what actually fires it. Tells the player to
// Equip; doesn't mention Cast or the candle at all yet.
export function getHasSeenEquipTutorial(): boolean {
  return hasSeenEquipTutorial;
}

export function markSeenEquipTutorial(): void {
  if (hasSeenEquipTutorial) return;
  hasSeenEquipTutorial = true;
  localStorage.setItem(EQUIP_TUTORIAL_KEY, 'true');
}

// Gates the second panel, shown a few ticks after the player actually equips
// both runes into the same hotbar slot (see ui.ts's closePanel) -- explains
// Cast and nudges toward the candle, now that there's something to cast at.
export function getHasSeenCastTutorial(): boolean {
  return hasSeenCastTutorial;
}

export function markSeenCastTutorial(): void {
  if (hasSeenCastTutorial) return;
  hasSeenCastTutorial = true;
  localStorage.setItem(CAST_TUTORIAL_KEY, 'true');
}

// Gates the popup shown a couple of seconds after the player learns their
// first Modifier rune (see ui.ts) -- explains that a third rune category
// exists, distinct from Concept/Operator, without naming what any specific
// modifier does.
export function getHasSeenModifierTutorial(): boolean {
  return hasSeenModifierTutorial;
}

export function markSeenModifierTutorial(): void {
  if (hasSeenModifierTutorial) return;
  hasSeenModifierTutorial = true;
  localStorage.setItem(MODIFIER_TUTORIAL_KEY, 'true');
}

// Gates the popup shown a couple of seconds after the player learns
// Transfer -- explains Mark as Source mechanically, the one rune whose
// controls aren't discoverable just from Equip/Cast alone.
export function getHasSeenTransferTutorial(): boolean {
  return hasSeenTransferTutorial;
}

export function markSeenTransferTutorial(): void {
  if (hasSeenTransferTutorial) return;
  hasSeenTransferTutorial = true;
  localStorage.setItem(TRANSFER_TUTORIAL_KEY, 'true');
}

// Gates the popup shown a couple of seconds after the player takes room2's
// Warded Key out of chest-2 (see ui.ts's resolveInteractText) -- by that point
// they've necessarily read both of room2's bookshelves (Energy + Project are
// what light and melt the Guttering Candle that was blocking the way), so
// this is the first natural pause with two real runes in hand. Points at the
// Runes tab's "Name this rune" field and the fact that whatever's typed there
// follows the rune to its chip everywhere, hotbar included -- a real feature
// with no other onboarding nudge anywhere.
export function getHasSeenNotebookTutorial(): boolean {
  return hasSeenNotebookTutorial;
}

export function markSeenNotebookTutorial(): void {
  if (hasSeenNotebookTutorial) return;
  hasSeenNotebookTutorial = true;
  localStorage.setItem(NOTEBOOK_TUTORIAL_KEY, 'true');
}

export function resetTutorialHints(): void {
  hasInteractedOnce = false;
  hasSeenEquipTutorial = false;
  hasSeenCastTutorial = false;
  hasSeenModifierTutorial = false;
  hasSeenTransferTutorial = false;
  hasSeenNotebookTutorial = false;
  localStorage.setItem(INTERACTED_KEY, 'false');
  localStorage.setItem(EQUIP_TUTORIAL_KEY, 'false');
  localStorage.setItem(CAST_TUTORIAL_KEY, 'false');
  localStorage.setItem(MODIFIER_TUTORIAL_KEY, 'false');
  localStorage.setItem(TRANSFER_TUTORIAL_KEY, 'false');
  localStorage.setItem(NOTEBOOK_TUTORIAL_KEY, 'false');
}
